import AbortController from 'abort-controller';
import { isDefined, isNotDefined, resolve, randomString, listToMap } from '@togglecorp/fujs';
import React__default, { createContext } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

var methods;
(function (methods) {
    methods["POST"] = "POST";
    methods["GET"] = "GET";
    methods["PUT"] = "PUT";
    methods["DELETE"] = "DELETE";
    methods["PATCH"] = "PATCH";
})(methods || (methods = {}));
function parseUrlParams(stringParams) {
    const params = decodeURIComponent(stringParams).split('&');
    let paramsJson = {};
    params.forEach((param) => {
        const split = param.split('=');
        paramsJson = {
            ...paramsJson,
            [split[0]]: split[1],
        };
    });
    return paramsJson;
}
function prepareUrlParams(params) {
    return Object.keys(params)
        .filter(k => isDefined(params[k]))
        .map((k) => {
        const param = params[k];
        if (isNotDefined(param)) {
            return undefined;
        }
        let val;
        if (Array.isArray(param)) {
            val = param.join(',');
        }
        else if (typeof param === 'number' || typeof param === 'boolean') {
            val = String(param);
        }
        else {
            val = param;
        }
        return `${encodeURIComponent(k)}=${encodeURIComponent(val)}`;
    })
        .filter(isDefined)
        .join('&');
}
class RestRequest {
    constructor({ key, url, params, onSuccess, onFailure, onFatal, onAbort, onInitialize, onPreLoad, onPostLoad, onAfterLoad, shouldRetry = true, retryTime = 1000, maxRetryAttempts = 5, pollTime = 4000, maxPollAttempts, shouldPoll, delay = 50, logWarning = true, logInfo = false, }) {
        this.start = () => {
            if (this.requestAborted) {
                this.consoleWarn((urlValue, parameters) => [
                    `Trying to start aborted request: ${urlValue}`, parameters
                ]);
                return;
            }
            if (this.requestRunning) {
                this.consoleWarn((urlValue, parameters) => [
                    `Trying to start running request: ${urlValue}`, parameters
                ]);
                return;
            }
            this.setRunningStart();
            if (this.onInitialize) {
                this.onInitialize(this.key);
            }
            this.retryId = window.setTimeout(this.request, this.delay);
        };
        this.stop = () => {
            if (!this.requestRunning) {
                return;
            }
            clearTimeout(this.pollId);
            clearTimeout(this.retryId);
            this.requestAborted = true;
            this.controller.abort();
            this.consoleLog(() => {
                const urlValue = resolve(this.url, this.key);
                return [`Stopping: ${urlValue}`];
            });
            if (this.onAbort) {
                this.onAbort(this.key);
            }
        };
        this.request = async () => {
            if (this.onPreLoad) {
                this.onPreLoad(this.key);
            }
            const urlValue = resolve(this.url, this.key);
            const parameters = resolve(this.params, this.key);
            this.consoleLog(() => [`Fetching: ${urlValue}`, parameters]);
            let response;
            try {
                const { signal } = this.controller;
                response = await fetch(urlValue, { ...parameters, signal });
                if (this.requestAborted) {
                    this.setRunningComplete();
                    return;
                }
            }
            catch (ex) {
                if (this.requestAborted) {
                    this.setRunningComplete();
                    return;
                }
                console.error(ex);
                const retrySuccessful = this.shouldRetry && this.retry();
                if (!retrySuccessful) {
                    this.handleFatal({
                        errorMessage: ex.message,
                        errorCode: ex.statusCode,
                    });
                }
                return;
            }
            let responseBody = {};
            try {
                const responseText = await response.text();
                if (this.requestAborted) {
                    this.setRunningComplete();
                    return;
                }
                if (responseText.length > 0) {
                    responseBody = JSON.parse(responseText);
                }
            }
            catch (ex) {
                if (this.requestAborted) {
                    this.setRunningComplete();
                    return;
                }
                console.error(ex);
                this.handleFatal({
                    errorMessage: 'Error while parsing json',
                    errorCode: null,
                });
                return;
            }
            this.consoleLog(() => [`Receiving: ${urlValue}`, responseBody]);
            if (response.ok) {
                if (this.shouldPoll && this.shouldPoll(responseBody, response.status)) {
                    this.poll();
                }
                else {
                    this.handleSuccess(responseBody, response.status);
                }
                return;
            }
            const is5xxError = Math.floor(response.status / 100) === 5;
            if (!is5xxError) {
                this.handleFailure(responseBody, response.status);
                return;
            }
            const retrySuccessful = this.shouldRetry && this.retry();
            if (!retrySuccessful) {
                this.handleFailure(responseBody, response.status);
            }
        };
        this.retry = () => {
            if (this.maxRetryAttempts >= 0 && this.retryCount > this.maxRetryAttempts) {
                this.consoleWarn((urlValue, parameters) => [
                    `Max retries exceeded: ${urlValue}`, parameters
                ]);
                return false;
            }
            this.retryId = window.setTimeout(this.request, this.retryTime);
            this.retryCount += 1;
            return true;
        };
        this.poll = () => {
            if (this.maxPollAttempts && this.pollCount > this.maxPollAttempts) {
                this.consoleWarn((urlValue, parameters) => [
                    `Max polls exceeded: ${urlValue}`, parameters
                ]);
                return;
            }
            this.pollCount += 1;
            this.pollId = window.setTimeout(this.request, this.pollTime);
        };
        this.setRunningStart = () => {
            this.requestRunning = true;
        };
        this.setRunningComplete = () => {
            this.requestRunning = false;
            this.requestAborted = false;
        };
        this.consoleLog = (method) => {
            if (this.logInfo) {
                console.log('RestRequest:', ...method());
            }
        };
        this.consoleWarn = (method) => {
            if (this.logWarning) {
                const urlValue = resolve(this.url, this.key);
                const parameters = resolve(this.params, this.key);
                console.warn('RestRequest:', ...method(urlValue, parameters));
            }
        };
        this.handleSuccess = (responseBody, status = 0) => {
            if (this.onPostLoad) {
                this.onPostLoad(this.key);
            }
            if (this.onSuccess) {
                this.onSuccess(this.key, responseBody, status);
            }
            else {
                this.consoleWarn((urlValue, parameters) => [
                    `No success callback defined: ${urlValue}`, parameters
                ]);
            }
            if (this.onAfterLoad) {
                this.onAfterLoad(this.key);
            }
            this.setRunningComplete();
        };
        this.handleFailure = (responseBody, status = 0) => {
            if (this.onPostLoad) {
                this.onPostLoad(this.key);
            }
            if (this.onFailure) {
                this.onFailure(this.key, responseBody, status);
            }
            else {
                this.consoleWarn((urlValue, parameters) => [
                    `No failure callback defined: ${urlValue}`, parameters
                ]);
            }
            if (this.onAfterLoad) {
                this.onAfterLoad(this.key);
            }
            this.setRunningComplete();
        };
        this.handleFatal = (responseBody, status = 0) => {
            if (this.onPostLoad) {
                this.onPostLoad(this.key);
            }
            if (this.onFatal) {
                this.onFatal(this.key, responseBody, status);
            }
            else {
                this.consoleWarn((urlValue, parameters) => [
                    `No fatal callback defined: ${urlValue}`, parameters
                ]);
            }
            if (this.onAfterLoad) {
                this.onAfterLoad(this.key);
            }
            this.setRunningComplete();
        };
        this.key = key;
        this.url = url;
        this.params = params;
        this.logWarning = logWarning;
        this.logInfo = logInfo;
        this.pollTime = pollTime;
        this.maxPollAttempts = maxPollAttempts;
        this.shouldPoll = shouldPoll;
        this.retryTime = retryTime;
        this.maxRetryAttempts = maxRetryAttempts;
        this.shouldRetry = shouldRetry;
        this.delay = delay;
        this.onInitialize = onInitialize;
        this.onAbort = onAbort;
        this.onPreLoad = onPreLoad;
        this.onSuccess = onSuccess;
        this.onFailure = onFailure;
        this.onFatal = onFatal;
        this.onPostLoad = onPostLoad;
        this.onAfterLoad = onAfterLoad;
        this.pollCount = 1;
        this.retryCount = 1;
        this.requestRunning = false;
        this.requestAborted = false;
        this.controller = new AbortController();
    }
}

const defaultState = {
    startRequest: () => { },
    stopRequest: () => { },
    state: {},
};
const RequestContext = createContext(defaultState);

const emptyObject = {};
function createRequestClient(requests = {}, consume) {
    return (WrappedComponent) => {
        const requestKeys = Object.keys(requests);
        const requestsOnMount = requestKeys.filter(key => requests[key].onMount);
        const requestsNonPersistent = requestKeys.filter(key => !requests[key].isPersistent);
        const requestsConsumed = consume || requestKeys;
        class View extends React__default.PureComponent {
            constructor(props) {
                super(props);
                this.lastProps = {};
                this.defaultParamsPerRequest = {};
                this.generateCanonicalKeys = (keys) => {
                    const seed = randomString(16);
                    return listToMap(keys, key => key, key => (requests[key].isUnique ? key : `${seed}-${key}`));
                };
                this.getCanonicalKey = (key) => (this.canonicalKeys[key] || key);
                this.getParams = (key, params) => ({
                    ...this.defaultParams,
                    ...this.defaultParamsPerRequest[key],
                    ...params,
                });
                this.setDefaultParamsPerRequest = (key, params) => {
                    this.defaultParamsPerRequest[key] = params;
                };
                this.setDefaultRequestParams = (params) => {
                    this.defaultParams = params;
                };
                this.startRequest = (key, params, ignoreIfExists) => {
                    const request = requests[key];
                    const myArgs = {
                        params,
                        props: this.getProps(this.props),
                    };
                    const { isUnique, onPropsChanged, onMount, isPersistent, onSuccess, onFailure, onFatal, group, method, url, query, body, options, extras, ...otherProps } = request;
                    const { context: { startRequest }, } = this;
                    const accessKey = this.getCanonicalKey(key);
                    startRequest({
                        key: accessKey,
                        group: resolve(group, myArgs),
                        method: resolve(method, myArgs),
                        url: resolve(url, myArgs),
                        query: resolve(query, myArgs),
                        body: resolve(body, myArgs),
                        options: resolve(options, myArgs),
                        extras: resolve(extras, myArgs),
                        onSuccess: onSuccess
                            ? (args) => {
                                onSuccess({ ...args, ...myArgs });
                            }
                            : undefined,
                        onFailure: onFailure
                            ? (args) => {
                                onFailure({ ...args, ...myArgs });
                            }
                            : undefined,
                        onFatal: onFatal
                            ? (args) => {
                                onFatal({ ...args, ...myArgs });
                            }
                            : undefined,
                        ...otherProps,
                    }, ignoreIfExists);
                };
                this.stopRequest = (key) => {
                    const { context: { stopRequest }, } = this;
                    const accessKey = this.getCanonicalKey(key);
                    stopRequest(accessKey);
                };
                this.readRequestState = (key) => {
                    const accessKey = this.getCanonicalKey(key);
                    const { context: { state: { [accessKey]: prop = emptyObject, }, }, } = this;
                    return prop;
                };
                this.calculatePropForKey = (key) => {
                    const prop = this.readRequestState(key);
                    let value = this.lastProps[key];
                    const changed = prop !== value;
                    if (changed) {
                        const { initialized } = this.state;
                        value = {
                            pending: !initialized && requestsOnMount.includes(key),
                            ...prop,
                            setDefaultParams: (params) => (this.setDefaultParamsPerRequest(key, params)),
                            do: (params) => (this.startRequest(key, this.getParams(key, params))),
                            abort: () => {
                                this.stopRequest(key);
                            },
                        };
                        this.lastProps[key] = value;
                    }
                    return {
                        key,
                        changed,
                        value,
                    };
                };
                this.calculateRequests = () => {
                    const props = requestsConsumed.map(this.calculatePropForKey);
                    if (props.every(prop => !prop.changed)) {
                        return this.lastProps;
                    }
                    return listToMap(props, prop => prop.key, prop => prop.value);
                };
                this.getProps = (props) => (Object.assign({
                    setDefaultRequestParams: this.setDefaultRequestParams,
                    requests: this.calculateRequests(),
                }, props));
                this.canonicalKeys = this.generateCanonicalKeys(requestKeys);
                this.state = {
                    initialized: false,
                };
            }
            componentDidMount() {
                const props = this.getProps(this.props);
                requestsOnMount.forEach((key) => {
                    const args = {
                        params: this.getParams(key),
                        props,
                    };
                    const { onMount } = requests[key];
                    if (!!onMount && resolve(onMount, args)) {
                        this.startRequest(key, args.params, requests[key].isUnique);
                    }
                    else {
                        this.stopRequest(key);
                    }
                });
                this.setState({ initialized: true });
            }
            componentDidUpdate(prevProps) {
                requestKeys.forEach((key) => {
                    const { onPropsChanged } = requests[key];
                    if (!onPropsChanged) {
                        return;
                    }
                    const propNames = Array.isArray(onPropsChanged)
                        ? onPropsChanged
                        : Object.keys(onPropsChanged);
                    const args = {
                        prevProps,
                        props: this.getProps(this.props),
                        params: this.getParams(key),
                    };
                    const makeRequest = propNames.some((propName) => {
                        const { props } = this;
                        const isModified = props[propName] !== prevProps[propName];
                        if (!isModified) {
                            return false;
                        }
                        if (!Array.isArray(onPropsChanged)) {
                            const onPropsChangedForProp = onPropsChanged[propName];
                            if (!onPropsChangedForProp) {
                                return false;
                            }
                            return resolve(onPropsChangedForProp, args);
                        }
                        return true;
                    });
                    if (makeRequest) {
                        this.startRequest(key, args.params);
                    }
                });
            }
            componentWillUnmount() {
                requestsNonPersistent.forEach((key) => {
                    this.stopRequest(key);
                });
            }
            render() {
                const props = this.calculateRequests();
                return (React__default.createElement(WrappedComponent, Object.assign({ setDefaultRequestParams: this.setDefaultRequestParams, requests: props }, this.props)));
            }
        }
        View.contextType = RequestContext;
        return hoistNonReactStatics(View, WrappedComponent);
    };
}

const emptyObject$1 = {};
const createRequestCoordinator = (attributes) => (WrappedComponent) => {
    const { transformParams, transformResponse, transformErrors, transformProps, transformUrl, onFailure: onFailureDefault, onFatal: onFatalDefault, } = attributes;
    class Coordinator extends React__default.Component {
        constructor(props) {
            super(props);
            this.mounted = false;
            this.requests = {};
            this.requestGroups = {};
            this.forEachRequest = (callback) => {
                Object.keys(this.requests).forEach((key) => {
                    callback(this.requests[key]);
                });
            };
            this.stopRequest = (key) => {
                this.setState({ [key]: { pending: false } }, () => {
                    const request = this.requests[key];
                    if (request) {
                        request.stop();
                    }
                });
            };
            this.startRequest = (requestData, ignoreIfExists) => {
                const { key, group, url, query, options = {}, } = requestData;
                const oldRequest = this.requests[key];
                if (oldRequest && oldRequest.running) {
                    if (ignoreIfExists) {
                        return;
                    }
                    oldRequest.stop();
                }
                const calculateParams = () => (transformParams(requestData, this.props));
                const appendage = query && prepareUrlParams(query);
                const preparedUrl = appendage && appendage.length > 0 ? `${url}?${appendage}` : url;
                const request = new RestRequest({
                    ...options,
                    key,
                    url: transformUrl ? transformUrl(preparedUrl, this.props) : preparedUrl,
                    params: calculateParams,
                    onPreLoad: this.handleRequestStart,
                    onInitialize: this.handleRequestStart,
                    onAfterLoad: this.handleRequestDone,
                    onAbort: this.handleRequestDone,
                    onSuccess: this.handleSuccess,
                    onFailure: this.handleFailure,
                    onFatal: this.handleFatal,
                });
                this.requests[key] = {
                    data: requestData,
                    running: false,
                    stop: () => {
                        request.stop();
                        this.requests[key].running = false;
                    },
                    start: () => {
                        this.requests[key].running = true;
                        request.start();
                    },
                };
                if (group) {
                    if (!this.requestGroups[group]) {
                        this.requestGroups[group] = [key];
                    }
                    else {
                        this.requestGroups[group].push(key);
                    }
                }
                if (this.mounted) {
                    this.requests[key].start();
                }
            };
            this.handleRequestStart = (key) => {
                const newState = { pending: true };
                const { data: { group } } = this.requests[key];
                const groupState = group ? {
                    [group]: {
                        pending: true,
                    },
                } : emptyObject$1;
                this.setState({ [key]: newState, ...groupState });
            };
            this.handleRequestDone = (key) => {
                const { state } = this;
                const requestState = state[key] || emptyObject$1;
                const newState = {
                    ...requestState,
                    pending: false,
                };
                const { data: { group } } = this.requests[key];
                const groupState = group ? {
                    [group]: {
                        pending: this.requestGroups[group]
                            .filter(k => k !== key)
                            .some(k => !!state[k].pending),
                    },
                } : emptyObject$1;
                this.setState({ [key]: newState, ...groupState });
            };
            this.handleSuccess = (key, body, status) => {
                const { data } = this.requests[key];
                let response;
                try {
                    response = transformResponse ? transformResponse(body, data) : body;
                }
                catch (e) {
                    this.handleFatal(key, e, 0);
                    return;
                }
                const { onSuccess } = data;
                if (onSuccess) {
                    onSuccess({ response, status });
                }
                const { state } = this;
                const requestState = state[key] || emptyObject$1;
                const newState = {
                    ...requestState,
                    response,
                    responseError: undefined,
                    responseStatus: status,
                };
                this.setState({ [key]: newState });
            };
            this.handleFailure = (key, body, status) => {
                const { data } = this.requests[key];
                let error;
                try {
                    error = transformErrors ? transformErrors(body, data) : body;
                }
                catch (e) {
                    this.handleFatal(key, e, 0);
                    console.error(e);
                    return;
                }
                const { onFailure } = data;
                if (onFailure) {
                    onFailure({ error, status });
                }
                else if (onFailureDefault) {
                    onFailureDefault({ error, status });
                }
                const { state } = this;
                const requestState = state[key] || emptyObject$1;
                const newState = {
                    ...requestState,
                    response: undefined,
                    responseError: error,
                    responseStatus: status,
                };
                this.setState({ [key]: newState });
            };
            this.handleFatal = (key, error, status) => {
                const { data } = this.requests[key];
                const { onFatal } = data;
                if (onFatal) {
                    onFatal({ error });
                }
                else if (onFatalDefault) {
                    onFatalDefault({ error });
                }
                const { state } = this;
                const requestState = state[key] || emptyObject$1;
                const newState = {
                    ...requestState,
                    response: undefined,
                    responseError: error,
                    responseStatus: status,
                };
                this.setState({ [key]: newState });
            };
            this.state = {};
        }
        componentDidMount() {
            this.mounted = true;
            this.forEachRequest(request => request.start());
        }
        componentWillUnmount() {
            this.forEachRequest(request => request.stop());
            this.mounted = false;
        }
        render() {
            const contextApi = {
                startRequest: this.startRequest,
                stopRequest: this.stopRequest,
                state: this.state,
            };
            const newProps = transformProps(this.props);
            return (React__default.createElement(RequestContext.Provider, { value: contextApi },
                React__default.createElement(WrappedComponent, Object.assign({}, newProps))));
        }
    }
    return hoistNonReactStatics(Coordinator, WrappedComponent);
};

const requests = {
    request: {
        onMount: true,
        onPropsChanged: ['url', 'query', 'body'],
        url: ({ props }) => props.url,
        body: ({ props }) => props.body,
        query: ({ props }) => props.query,
        method: ({ props }) => props.method,
    },
};
class HandlerComponent extends React__default.PureComponent {
    constructor(props) {
        super(props);
        props.onRequestChange(props.requests.request, props.changeParams);
    }
    componentWillReceiveProps(nextProps) {
        const { requests: oldRequests, } = this.props;
        const { requests: newRequests, changeParams, } = nextProps;
        if (oldRequests.request !== newRequests.request) {
            nextProps.onRequestChange(newRequests.request, changeParams);
        }
    }
    render() {
        return null;
    }
}
const RequestHandler = createRequestClient(requests)(HandlerComponent);

export { RequestHandler, RestRequest, createRequestClient, createRequestCoordinator, methods, parseUrlParams, prepareUrlParams };
